import { Account, ComparisonReportData } from "types/Models/report";

export type MonthData = {
  summary_amount: number;
  summary_variance_amount?: number;
  summary_variance_percentage?: number;
};

export type AccountWithMonths = Omit<Account, "types"> & {
  types: AccountWithMonths[];
  parent_key?: string;
  monthData?: Record<string, MonthData>;
};

function getAccountKey(acc: { name: string; identifier: number | null }) {
  return `${acc.name}:${acc.identifier}`;
}

function mergeAccounts(
  target: AccountWithMonths,
  source: Account,
  month: string
) {
  // Initialize monthData if it doesn't exist
  if (!target.monthData) {
    target.monthData = {};
  }

  const sourceWithVariance = source as Account & {
    summary_variance_amount?: number;
    summary_variance_percentage?: number;
  };

  // Add month data for this node
  target.monthData[month] = {
    summary_amount: source.summary_amount,
    ...(sourceWithVariance.summary_variance_amount !== undefined && {
      summary_variance_amount: sourceWithVariance.summary_variance_amount,
    }),
    ...(sourceWithVariance.summary_variance_percentage !== undefined && {
      summary_variance_percentage:
        sourceWithVariance.summary_variance_percentage,
    }),
  };

  // Merge children by composite key
  for (const child of source.types) {
    const childKey = getAccountKey(child);
    let existing = target.types.find((t) => getAccountKey(t) === childKey);
    if (!existing) {
      // Shallow copy for new child
      existing = {
        ...child,
        types: [],
        parent_key: getAccountKey(source),
      } as AccountWithMonths;
      target.types.push(existing);
    }
    mergeAccounts(existing, child, month);
  }
}

export function buildUnifiedTree(
  report_data: ComparisonReportData
): AccountWithMonths[] {
  // Map of key to node reference
  const allNodes = new Map<string, AccountWithMonths>();
  // Map of key to parent key
  const parentMap = new Map<string, string | undefined>();

  // Pass 1: merge all data into one node per key
  for (const month in report_data) {
    const monthData = report_data[month];
    if (!monthData) continue;
    const accounts = monthData.accounts;
    function walk(acc: Account, parent_key?: string) {
      const key = getAccountKey(acc);
      let node = allNodes.get(key);
      if (!node) {
        node = {
          ...acc,
          types: [],
          parent_key,
        } as AccountWithMonths;
        allNodes.set(key, node);
        parentMap.set(key, parent_key);
      }
      mergeAccounts(node, acc, month);
      for (const child of acc.types) {
        walk(child, key);
      }
    }
    for (const acc of accounts) {
      walk(acc);
    }
  }

  // Pass 2: rebuild the unified tree from the map (attach children)
  allNodes.forEach((node) => (node.types = []));
  allNodes.forEach((node, key) => {
    const parent_key = parentMap.get(key);
    if (parent_key && allNodes.has(parent_key)) {
      const parent = allNodes.get(parent_key)!;
      parent.types.push(node);
    }
  });

  // Pass 3: collect root nodes (no parent, or parent not present)
  const rootAccounts: AccountWithMonths[] = [];
  allNodes.forEach((node, key) => {
    const parent_key = parentMap.get(key);
    if (!parent_key || !allNodes.has(parent_key)) {
      rootAccounts.push(node);
    }
  });

  return rootAccounts;
}

export const formatViewReportData = (
  reportData: ComparisonReportData
): AccountWithMonths[] => {
  if (!reportData || Object.keys(reportData).length === 0) {
    return [];
  }
  return buildUnifiedTree(reportData);
};
